import { BaseElements, BaseObject, BaseService, BaseInjector } from '../../js/base';
import { querySelectorAll, querySelector } from '../../utility/utils/index';
import PopNotification from '../../plugins/pop-notification';
import AuthorsTruncate from '../../plugins/authors-truncate';

declare global {
    interface Window { isShowRecommendedFetched: boolean; }
}

const events = {
    dataFetch: 'UX:data-fetch'
};

class Texts extends BaseObject { }

class ClassList extends BaseObject {
    base = 'show-recommended';
    delayLoad = 'delayLoad';
    delayedLoad = 'delayedLoad';
}

class Selectors extends BaseObject {
    lazyLoad = 'lazy-load';
    delayLoad = 'delayLoad';
}

class Elements extends BaseElements<Selectors> {
    protected _lazyload:NodeListOf<HTMLElement>;
    protected  _delayLoad:NodeListOf<HTMLElement>;
    wrapper: HTMLElement;

    get lazyload(): NodeListOf<HTMLElement> {
        return this.lazyload;
    }

    set lazyload(value:NodeListOf<HTMLElement>)  {
         this._lazyload = value;
         value?.forEach(el => el.parentNode.removeChild(el));
    }

    get delayLoad(): NodeListOf<HTMLElement> {
        return this._delayLoad;
    }

    set delayLoad(value:NodeListOf<HTMLElement>)  {
         this._delayLoad = value;
    }

    constructor(selectors: Selectors) {
        super(selectors, false);
    }

    setWrapper(wrapper: HTMLElement) {
        this.wrapper = wrapper;
       
    }

    initElements() {
        this.lazyload = querySelectorAll(this.selectors.lazyLoad, this.wrapper);
        this.delayLoad = querySelectorAll(this.selectors.delayLoad, this.wrapper);

    }
}

interface ShowRecommended extends BaseService<Selectors, Elements, ClassList, Texts> { }


@BaseInjector(Selectors, Elements, ClassList, Texts)
class ShowRecommended {

    get removeShowRecommendedIfNoData() {
        return true;
    }

    get ajaxUrl() {
        return this.wrapper.dataset.ajaxurl;
    }

    constructor(readonly wrapper: HTMLElement) { }

    initialize(): void {
        this.elements.setWrapper(this.wrapper);
        window.isShowRecommendedFetched = false;

        this.addEventListeners();
    }

    addEventListeners(): void {
        this.fetchContent().then(() => {
            this.afterAjax();
        });
    }

    async fetchContent() {
        try {
            const response = await fetch(this.ajaxUrl);
            if (response.status !== 200) {
                throw new Error('Failed to load content');
            }
            const content = await response.text();
            this.wrapper.innerHTML = content;
        } catch (e) {
        }
    }

    afterAjax() {
        const popupNotification = querySelectorAll('.show-recommended.pop-notification');    
        if (popupNotification.length) {
            popupNotification.forEach(item => {
                new PopNotification(item).initialize();
            });
        } 
        const showRecommended = querySelector('.show-recommended');
        let authors = querySelectorAll('.authors', showRecommended); 
        if (authors.length) {
            new AuthorsTruncate(authors).initialize();
        }
        setTimeout(() => {
            this.hideSkeletons();
            this.emitFetchedEvent();
        }, 10)
    }

    emitFetchedEvent() {
        const fetchedEvent = new Event(events.dataFetch);
        this.wrapper.dispatchEvent(fetchedEvent);
        // Sometimes other JS components loads after this component therefore the event won't be listened to, so a global variable
        // would be helpful as well
        window.isShowRecommendedFetched = true;
    }

    hideSkeletons() {
        this.elements.initElements();
        this.elements.delayLoad.forEach(el => {
            el.classList.remove(this.classList.delayLoad);
            el.classList.add(this.classList.delayedLoad)
        });

        if (this.removeShowRecommendedIfNoData) {
            // Check that the response has actual data
            if (this.wrapper.children[0] && this.wrapper.children[0].children[0]) {
                this.wrapper.replaceWith(...this.base.convertToArray(this.wrapper.children));
            } else {
                this.wrapper.parentElement.classList.contains(this.classList.base) ? this.wrapper.parentElement.remove() : '';
            }
        } else {
            this.wrapper.replaceWith(...this.base.convertToArray(this.wrapper.children));
        }
    }

}

export default ShowRecommended;

export {
    Elements as ShowRecommendedElements,
    Selectors as ShowRecommendedSelectors,
    ClassList as ShowRecommendedClassList,
    Texts as ShowRecommendedTexts,
    events,
}